import { useEffect, useState } from "react";
import { connect } from "react-redux";
import { useIntl } from "react-intl";
import { Button } from "primereact/button";
import { useNavigate, useParams } from "react-router-dom";
import { Editor } from "../../../common/fields";
import CustomBreadCrumbs from "../../../common/breadcrumbs";
import Form from "../../../common/form";
import {
  resetUpload,
  uploadRequest,
} from "../../../../store/slices/uploadSlice";
import {
  createRequest,
  detailsRequest,
  updateDetailRequest,
} from "../../../../store/slices/learningObjectsSlice";
import LearningObjectDetailsForm from "./LearningObjectDetailsForm";
import Section from "../../../common/section";
import {
  AccessScopeConstants,
  GlobalConstants,
  LearningObjectConstants,
} from "../../../../constants";
import FileUpload from "../../../common/fileUpload";
import * as RoleHelper from "../../../../helpers/RoleHelper";
import { listRequest as certificateListRequest } from "../../../../store/slices/certificateSlice";
import { useMountEffect } from "primereact/hooks";

const LearningObjectDetails = ({
  action,
  certificateListRequest,
  certificates,
  createRequest,
  detailsRequest,
  enterprise,
  error,
  labels,
  menu,
  progress,
  record,
  requesting,
  resetUpload,
  updateDetailRequest,
  uploadDetails,
  uploadErrors,
  uploading,
  uploadRequest,
  user,
}) => {
  const intl = useIntl();

  const params = useParams();
  const [fileName, setFileName] = useState("");
  const [bannerFileName, setBannerFileName] = useState("");
  const navigate = useNavigate();

  const [uploadType, setUploadType] = useState(null);
  const [isEditable, setIsEditable] = useState(params?.id ? false : true);
  const [htmlContent, setHtmlContent] = useState(record?.content || "");

  const { role } = user || {};
  const canUpdate = RoleHelper.checkRoleScope(
    role?.scope,
    AccessScopeConstants.ContentAccess,
    AccessScopeConstants.UpdateScope
  );

  useMountEffect(() => {
    resetUpload();
    if (params?.id) {
      detailsRequest({ id: params.id });
    }
    certificateListRequest();
  });

  useEffect(() => {
    if ((action === "create" || action === "update") && !requesting && !error) {
      navigate(menu.to);
    }
  }, [error, action, requesting, menu, navigate]);

  const selectedType = LearningObjectConstants.TYPES.find(
    (type) =>
      type.value === uploadType || record?.type?.toLowerCase() === type.name
  );

  useEffect(() => {
    const HTMLType = LearningObjectConstants.TYPES.find(
      (item) => item.id === 4
    );

    if (record?.type.toLowerCase() === HTMLType.name.toLocaleLowerCase()) {
      setHtmlContent(record?.content);
    }
  }, [record]);

  const fileType = selectedType?.contentType || "*/*";

  const onBannerUpload = (event) => {
    const file = event.files[0];
    setBannerFileName(file.name);
    uploadRequest({ file, type: "image" });
  };

  const onUpload = (event) => {
    const file = event.files[0];
    setFileName(file.name);
    const type = uploadType;
    uploadRequest({ file, type });
  };

  const onSubmit = (values) => {
    const staticContentType = uploadType === 4 ? "text/html" : "";

    const payload = {
      ...values,
      bannerImageId:
        uploadDetails?.[bannerFileName]?.artifactId ||
        record?.bannerImageId ||
        null,
      isCertified: false,
      supportedLanguages: values.supportedLanguages.map((lang) => lang.code),
      certificateId:
        values?.certificateId === "no-cert"
          ? null
          : values.certificateId || null,
      maxAttemptsAllowed: 0,
      pointsOnCompletion: 0,
      maxTimeInSecsToComplete: values?.maxTimeInSecsAllowed * 60,
      maxTimeInSecsAllowed: values?.maxTimeInSecsAllowed * 60,
      masteryScore:
        parseInt(uploadDetails?.[fileName]?.masteryScore) ||
        record?.masteryScore,
      artifactId: uploadDetails?.[fileName]?.artifactId || record?.artifactId,
      manifestFilePath:
        uploadDetails?.[fileName]?.manifestFilePath || record?.manifestFilePath,
      contentType:
        uploadDetails?.[fileName]?.contentType ||
        uploadDetails?.[fileName]?.courseType ||
        staticContentType ||
        record?.contentType,
      content: uploadType === 4 ? htmlContent : null,
    };
    if (!params.id) {
      createRequest(payload);
    } else {
      updateDetailRequest({ id: params.id, payload });
    }
  };

  const uploadableTypes = LearningObjectConstants.TYPES.filter(
    (item) => item.contentType
  ).map((item) => item.id);

  return (
    <div className="pb-8 md:pb-0">
      <CustomBreadCrumbs
        labels={labels}
        navigate={navigate}
        editLabel={record?.name}
      />
      <div className="edexlms-page-content">
        <div className="edexlms-page-header">
          <div className="edexlms-page-title">
            {intl.formatMessage({
              id: params?.id ? "edit_learning_object" : "add_learning_object",
            })}
          </div>
          {(params?.id && !isEditable && (
            <div className="edexlms-page-actions">
              <Button
                icon="pi pi-pencil"
                outlined
                onClick={() =>
                  (record?.id && setIsEditable(!isEditable)) || null
                }
                label={intl.formatMessage({ id: "edit" })}
                type="button"
                size="small"
                disabled={!canUpdate || !record?.id}
                tooltipOptions={{ showOnDisabled: true, position: "bottom" }}
                tooltip={
                  canUpdate && !record?.isSystemDefined
                    ? ""
                    : intl.formatMessage({ id: "no_permission" })
                }
              />
            </div>
          )) ||
            null}
        </div>
        <div
          className="edexlms-page-body"
          style={{
            height: "calc(100vh - 64px - 20px - 45px - 40px - 14px - 33px)",
            overflowY: "scroll",
          }}
        >
          <Form
            initialValues={{
              certificateId: record?.certificateId || "no-cert",
              name: record?.name,
              type: selectedType?.id || null,
              maxAttemptsAllowed: record?.maxAttemptsAllowed,
              maxTimeInSecsAllowed:
                ((record?.maxTimeInSecsAllowed || 0) / 60).toFixed(0) || 0,
              pointsOnCompletion: record?.pointsOnCompletion,
              tags: record?.tags,
              supportedLanguages: GlobalConstants.LANGUAGES.filter((lang) =>
                (record?.supportedLanguages || ["en"]).includes(lang.code)
              ).map((language) => ({
                ...language,
                id: language.code,
                name: language.name,
              })),
            }}
            disabled={
              (!params.id &&
                ((uploadableTypes.includes(uploadType) &&
                  !uploadDetails?.[fileName]) ||
                  (uploadType === 4 && !htmlContent))) ||
              !isEditable
            }
            submitBtn={{
              label: intl.formatMessage({
                id: params?.id
                  ? "update_learning_object"
                  : "add_learning_object",
              }),
              onClick: onSubmit,
            }}
            cancelBtn={{
              label: intl.formatMessage({ id: "cancel" }),
              onClick: () => navigate(menu?.to),
            }}
          >
            <div className="flex flex-column gap-4">
              <LearningObjectDetailsForm
                setUploadType={setUploadType}
                uploadDetails={progress?.[fileName]}
                disabled={!isEditable}
                readOnly={params?.id ? true : false}
                certificates={[
                  {
                    name: intl.formatMessage({ id: "no_certificate" }),
                    id: "no-cert",
                  },
                  ...certificates,
                ]}
                allowedLanguages={
                  enterprise?.languageConfiguration?.allowedLanguages
                }
              />
              <Section
                title="content_details"
                titleValues={{
                  type:
                    (selectedType?.name &&
                      intl.formatMessage({
                        id: selectedType?.name,
                      })) ||
                    "",
                }}
              >
                <div className="grid">
                  <div className="col-12">
                    <FileUpload
                      name="banner_file"
                      label="banner_image"
                      infoMessages={[
                        intl.formatMessage({
                          id: `upload_type_info_image`,
                        }),
                        intl.formatMessage(
                          {
                            id: `upload_file_dimensions`,
                          },
                          {
                            width: 1350,
                            height: 450,
                          }
                        ),
                      ]}
                      fileType={"image/*"}
                      fileUrl={
                        (record?.bannerImage &&
                          `${new URL(
                            record?.bannerImage,
                            process.env.REACT_APP_URL
                          ).toString()}`) ||
                        null
                      }
                      uploading={uploading?.[bannerFileName]}
                      progress={progress?.[bannerFileName] || 0}
                      onUpload={onBannerUpload}
                      readOnly={params?.id ? !isEditable : false}
                      uploadDetails={uploadDetails?.[bannerFileName]}
                      onClear={() => setBannerFileName("")}
                      maxFileSize={524288000 / 10}
                    />
                  </div>
                  {(uploadType &&
                    uploadableTypes.includes(uploadType) &&
                    ((params?.id && isEditable) || !params?.id) && (
                      <div className="col-12">
                        <FileUpload
                          infoMessages={[
                            intl.formatMessage({
                              id: `upload_type_info_${selectedType?.name}`,
                            }),
                            intl.formatMessage(
                              {
                                id: `upload_file_size`,
                              },
                              { size: selectedType?.maxSize || "1.5 GB" }
                            ),
                          ]}
                          required={true}
                          name="file"
                          label="learning_object_content"
                          fileType={fileType}
                          uploading={uploading?.[fileName]}
                          progress={progress?.[fileName] || 0}
                          onUpload={onUpload}
                          readOnly={params?.id ? !isEditable : false}
                          error={uploadErrors?.[fileName]}
                          messageAfterUpload={
                            !uploadDetails?.[fileName]?.artifactId
                              ? "verify_scorm"
                              : ""
                          }
                          maxFileSize={selectedType?.maxSizeInBytes}
                          uploadDetails={uploadDetails?.[fileName]}
                          onClear={() => setFileName("")}
                        />
                      </div>
                    )) ||
                    null}
                  {(uploadType === 4 && isEditable && (
                    <div className="col-12">
                      <Editor
                        label={intl.formatMessage({ id: "html_content" })}
                        value={""}
                        required={true}
                        input={{
                          name: "html_content",
                          onChange: setHtmlContent,
                          value: htmlContent,
                        }}
                        style={{ height: "320px" }}
                      />
                    </div>
                  )) ||
                    null}
                </div>
              </Section>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  action: state.learningObjects?.action,
  certificates: state?.certificates?.data || [],
  enterprise: state?.global?.enterprise,
  error: state.learningObjects?.error,
  progress: state.uploads?.progress,
  record: state.learningObjects?.record,
  requesting: state.learningObjects?.requesting,
  uploadDetails: state.uploads?.details,
  uploadErrors: state.uploads?.error,
  uploading: state.uploads?.uploading,
  user: state.self?.user,
});

const mapDispatchToProps = (dispatch) => ({
  resetUpload: () => dispatch(resetUpload()),
  uploadRequest: (data) => dispatch(uploadRequest(data)),
  createRequest: (data) => dispatch(createRequest(data)),
  detailsRequest: (id) => dispatch(detailsRequest(id)),
  updateDetailRequest: (data) => dispatch(updateDetailRequest(data)),
  certificateListRequest: (data) => dispatch(certificateListRequest(data)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(LearningObjectDetails);
